import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  TextField,
  IconButton,
  Divider,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Add, Delete, Schedule as ScheduleIcon, EventBusy } from '@mui/icons-material';

export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface TimeSlot {
  open: string;  // "HH:MM"
  close: string; // "HH:MM"
}

export interface DaySchedule {
  closed: boolean;
  slots: TimeSlot[];
}

export interface ScheduleOverride {
  date: string; // "YYYY-MM-DD"
  closed: boolean;
  slots?: TimeSlot[];
  reason: { de: string; en: string };
}

export interface BranchSchedule {
  weekly: Record<DayKey, DaySchedule>;
  overrides: ScheduleOverride[];
}

const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DEFAULT_SCHEDULE: BranchSchedule = {
  weekly: {
    mon: { closed: true, slots: [] },
    tue: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '22:30' }] },
    wed: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '22:30' }] },
    thu: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '22:30' }] },
    fri: { closed: false, slots: [{ open: '11:30', close: '14:30' }, { open: '17:00', close: '23:00' }] },
    sat: { closed: false, slots: [{ open: '12:00', close: '23:00' }] },
    sun: { closed: false, slots: [{ open: '12:00', close: '22:30' }] },
  },
  overrides: [],
};

interface Props {
  open: boolean;
  branchId: string;
  branchName: string;
  onClose: () => void;
}

// Firestore rejects `undefined`. Recursively strip undefined fields so toggling
// an override between Closed/Open/Custom never leaves stale `slots: undefined`.
function stripUndefined<T>(val: T): T {
  if (Array.isArray(val)) {
    return val.map(stripUndefined).filter((x) => x !== undefined) as any;
  }
  if (val && typeof val === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(val)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out;
  }
  return val;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': { fontSize: '0.85rem', color: '#e0e0e0' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a3d52' },
  '& .MuiInputLabel-root': { color: '#9ca3af' },
};

const BranchScheduleDialog: React.FC<Props> = ({ open, branchId, branchName, onClose }) => {
  const { t, i18n } = useTranslation();
  const [schedule, setSchedule] = useState<BranchSchedule>(DEFAULT_SCHEDULE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [tab, setTab] = useState<'weekly' | 'holidays'>('weekly');

  useEffect(() => {
    if (!open || !branchId || branchId === 'default') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { doc, getDoc } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        const snap = await getDoc(doc(db, 'branches', branchId));
        if (cancelled) return;
        const data = snap.data() as any;
        const existing = data?.schedule as BranchSchedule | undefined;
        if (existing?.weekly) {
          // merge with default to handle missing days
          const merged: BranchSchedule = {
            weekly: { ...DEFAULT_SCHEDULE.weekly, ...existing.weekly },
            overrides: Array.isArray(existing.overrides) ? existing.overrides : [],
          };
          setSchedule(merged);
        } else {
          setSchedule(DEFAULT_SCHEDULE);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load schedule');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, branchId]);

  const updateDay = (day: DayKey, patch: Partial<DaySchedule>) => {
    setSchedule((s) => ({ ...s, weekly: { ...s.weekly, [day]: { ...s.weekly[day], ...patch } } }));
  };

  const addSlot = (day: DayKey) => {
    updateDay(day, { slots: [...schedule.weekly[day].slots, { open: '11:00', close: '22:00' }] });
  };
  const removeSlot = (day: DayKey, idx: number) => {
    updateDay(day, { slots: schedule.weekly[day].slots.filter((_, i) => i !== idx) });
  };
  const patchSlot = (day: DayKey, idx: number, patch: Partial<TimeSlot>) => {
    updateDay(day, {
      slots: schedule.weekly[day].slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    });
  };

  const addOverride = () => {
    const today = new Date();
    const iso = today.toISOString().slice(0, 10);
    setSchedule((s) => ({
      ...s,
      overrides: [...s.overrides, { date: iso, closed: true, reason: { de: '', en: '' } }],
    }));
  };
  const removeOverride = (idx: number) => {
    setSchedule((s) => ({ ...s, overrides: s.overrides.filter((_, i) => i !== idx) }));
  };
  const patchOverride = (idx: number, patch: Partial<ScheduleOverride>) => {
    setSchedule((s) => ({
      ...s,
      overrides: s.overrides.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  };

  const handleSave = async () => {
    if (!branchId || branchId === 'default') {
      setError('Branch must be saved first before editing its schedule.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      // Normalize overrides: if closed, drop slots entirely; if open without
      // slots, treat as closed so Firestore doesn't see undefined values.
      const cleanOverrides = [...schedule.overrides]
        .filter((o) => o.date)
        .map((o) => {
          if (o.closed) {
            return { date: o.date, closed: true, reason: o.reason || { de: '', en: '' } };
          }
          const slots = (o.slots || []).filter((s) => s && s.open && s.close);
          if (slots.length === 0) {
            return { date: o.date, closed: true, reason: o.reason || { de: '', en: '' } };
          }
          return { date: o.date, closed: false, slots, reason: o.reason || { de: '', en: '' } };
        })
        .sort((a, b) => a.date.localeCompare(b.date));

      // Normalize weekly: each day must have {closed, slots} with no undefined
      const cleanWeekly = Object.fromEntries(
        (Object.entries(schedule.weekly) as Array<[DayKey, DaySchedule]>).map(([k, d]) => {
          const closed = !!d.closed;
          const slots = closed ? [] : (d.slots || []).filter((s) => s && s.open && s.close);
          return [k, { closed: closed || slots.length === 0, slots }];
        }),
      ) as Record<DayKey, DaySchedule>;

      const clean: BranchSchedule = stripUndefined({ weekly: cleanWeekly, overrides: cleanOverrides });

      const { doc, updateDoc, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      await updateDoc(doc(db, 'branches', branchId), {
        schedule: clean,
        updated_at: serverTimestamp(),
      });
      setInfo(t('schedule.saved', 'Schedule saved'));
      setTimeout(() => onClose(), 700);
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const lang = (i18n.language || 'en').startsWith('ar') ? 'ar' : 'en';
  const dayLabel = (k: DayKey): string => t(`schedule.days.${k}`, {
    mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
  }[k]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: '#252838', borderRadius: 3, color: '#e0e0e0' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid #3a3d52', fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}>
        {t('schedule.title', 'Opening hours & closures')}
        <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5, fontSize: '0.85rem' }}>
          {branchName}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, md: 3 }, pt: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#00C896' }} />
          </Box>
        )}
        {!loading && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

            {/* Tabs: Weekly hours | Holidays & closures */}
            <Box sx={{
              display: 'flex', gap: 1, mb: 2.5,
              borderBottom: '1px solid #3a3d52',
            }}>
              {([
                { id: 'weekly', label: t('schedule.weeklyHours', 'Weekly hours'), icon: <ScheduleIcon fontSize="small" /> },
                { id: 'holidays', label: t('schedule.specialDates', 'Holidays & special closures'), icon: <EventBusy fontSize="small" />, badge: schedule.overrides.length },
              ] as const).map((tabDef) => {
                const active = tab === tabDef.id;
                return (
                  <Box
                    key={tabDef.id}
                    onClick={() => setTab(tabDef.id as 'weekly' | 'holidays')}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 2, py: 1.25,
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: active ? '#00C896' : '#9ca3af',
                      borderBottom: active ? '2px solid #00C896' : '2px solid transparent',
                      marginBottom: '-1px',
                      transition: 'color .15s, border-color .15s',
                      '&:hover': { color: active ? '#00C896' : '#e0e0e0' },
                      userSelect: 'none',
                      fontSize: '0.9rem',
                    }}
                  >
                    {tabDef.icon}
                    <span>{tabDef.label}</span>
                    {'badge' in tabDef && tabDef.badge ? (
                      <Chip
                        label={tabDef.badge}
                        size="small"
                        sx={{
                          height: 18, minWidth: 18, fontSize: '0.68rem', fontWeight: 700,
                          backgroundColor: active ? '#00C896' : '#3a3d52',
                          color: active ? '#1a1d2e' : '#e0e0e0',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: tab === 'weekly' ? 'flex' : 'none', flexDirection: 'column', gap: 1.25, mb: 3 }}>
              {DAY_KEYS.map((day) => {
                const d = schedule.weekly[day];
                return (
                  <Box key={day} sx={{
                    p: 1.5, borderRadius: 2, background: '#1e2139', border: '1px solid #3a3d52',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600, minWidth: 110 }}>{dayLabel(day)}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: d.closed ? '#ef4444' : '#9ca3af', fontSize: '0.85rem' }}>
                          {d.closed ? t('schedule.closed', 'Closed') : t('schedule.open', 'Open')}
                        </Typography>
                        <Switch
                          checked={!d.closed}
                          onChange={(_, v) => updateDay(day, { closed: !v, slots: !v ? [] : (d.slots.length ? d.slots : [{ open: '11:30', close: '22:00' }]) })}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#00C896' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00C896' } }}
                        />
                      </Box>
                    </Box>
                    {!d.closed && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {d.slots.map((slot, i) => {
                          const total = d.slots.length;
                          const slotLabel = total === 1
                            ? t('schedule.slotOpenAllDay', 'Open all day')
                            : total === 2
                              ? (i === 0 ? t('schedule.slotLunch', 'Lunch') : t('schedule.slotDinner', 'Dinner'))
                              : t('schedule.slotN', 'Shift {{n}}', { n: i + 1 });
                          return (
                            <Box key={i} sx={{
                              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                              p: 1, borderRadius: 1.5, background: '#252838',
                            }}>
                              <Chip
                                label={slotLabel}
                                size="small"
                                sx={{
                                  backgroundColor: '#00C89622',
                                  color: '#00C896',
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  height: 22,
                                  minWidth: total === 1 ? 100 : 70,
                                }}
                              />
                              <TextField
                                type="time"
                                size="small"
                                value={slot.open}
                                onChange={(e) => patchSlot(day, i, { open: e.target.value })}
                                sx={fieldSx}
                              />
                              <Typography sx={{ color: '#9ca3af', px: 0.5 }}>–</Typography>
                              <TextField
                                type="time"
                                size="small"
                                value={slot.close}
                                onChange={(e) => patchSlot(day, i, { close: e.target.value })}
                                sx={fieldSx}
                              />
                              {total > 1 && (
                                <IconButton size="small" onClick={() => removeSlot(day, i)} sx={{ color: '#ef4444', ml: 'auto' }}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          );
                        })}
                        {d.slots.length < 3 && (
                          <Button startIcon={<Add />} onClick={() => addSlot(day)} size="small"
                            sx={{ alignSelf: 'flex-start', color: '#00C896', textTransform: 'none', mt: 0.5 }}>
                            {d.slots.length === 0
                              ? t('schedule.addSlot', 'Add time slot')
                              : d.slots.length === 1
                                ? t('schedule.addBreak', '+ Add lunch/dinner break')
                                : t('schedule.addAnother', '+ Add another shift')}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: tab === 'holidays' ? 'block' : 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                {t('schedule.specialDates', 'Holidays & special closures')}
              </Typography>
              <Button startIcon={<Add />} onClick={addOverride} size="small" variant="outlined"
                sx={{
                  color: '#00C896', borderColor: '#00C89666', textTransform: 'none',
                  '&:hover': { borderColor: '#00C896', backgroundColor: '#00C89611' },
                }}>
                {t('schedule.addOverride', 'Add date')}
              </Button>
            </Box>
            {schedule.overrides.length === 0 && (
              <Box sx={{
                p: 3, borderRadius: 2, border: '1px dashed #3a3d52',
                textAlign: 'center', color: '#9ca3af', mb: 1.5,
              }}>
                <EventBusy sx={{ fontSize: 32, color: '#4a4d62', mb: 1 }} />
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#e0e0e0', mb: 0.5 }}>
                  {t('schedule.noOverridesTitle', 'No special dates yet')}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#9ca3af' }}>
                  {t('schedule.noOverrides', 'No special dates set. Add a holiday or event closure.')}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {schedule.overrides.map((o, idx) => (
                <Box key={idx} sx={{
                  p: 1.5, borderRadius: 2, background: '#1e2139', border: '1px solid #3a3d52',
                }}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={6} sm={3}>
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={o.date}
                        onChange={(e) => patchOverride(idx, { date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        label={t('schedule.date', 'Date')}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          checked={!o.closed}
                          onChange={(_, v) => patchOverride(idx, { closed: !v, slots: !v && !o.slots?.length ? [{ open: '11:30', close: '22:00' }] : o.slots })}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#00C896' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00C896' } }}
                        />
                        <Chip
                          size="small"
                          label={o.closed ? t('schedule.closed', 'Closed') : t('schedule.customHours', 'Open')}
                          sx={{
                            backgroundColor: o.closed ? '#ef4444' : '#00C896',
                            color: o.closed ? '#fff' : '#1a1d2e',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Feiertag"
                        label={t('schedule.reasonDe', 'Reason (DE)')}
                        InputLabelProps={{ shrink: true }}
                        value={o.reason.de}
                        onChange={(e) => patchOverride(idx, { reason: { ...o.reason, de: e.target.value } })}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Public holiday"
                        label={t('schedule.reasonEn', 'Reason (EN)')}
                        InputLabelProps={{ shrink: true }}
                        value={o.reason.en}
                        onChange={(e) => patchOverride(idx, { reason: { ...o.reason, en: e.target.value } })}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{ textAlign: 'right' }}>
                      <IconButton size="small" onClick={() => removeOverride(idx)} sx={{ color: '#ef4444' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Grid>
                    {!o.closed && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: { sm: 1 } }}>
                          <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                            {t('schedule.customHoursLabel', 'Custom hours for this date')}
                          </Typography>
                          {(o.slots || []).map((slot, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <TextField
                                type="time" size="small" value={slot.open}
                                onChange={(e) => patchOverride(idx, {
                                  slots: (o.slots || []).map((s, j) => j === i ? { ...s, open: e.target.value } : s),
                                })}
                                sx={fieldSx}
                              />
                              <Typography sx={{ color: '#9ca3af' }}>–</Typography>
                              <TextField
                                type="time" size="small" value={slot.close}
                                onChange={(e) => patchOverride(idx, {
                                  slots: (o.slots || []).map((s, j) => j === i ? { ...s, close: e.target.value } : s),
                                })}
                                sx={fieldSx}
                              />
                              <IconButton size="small" sx={{ color: '#ef4444' }}
                                onClick={() => patchOverride(idx, { slots: (o.slots || []).filter((_, j) => j !== i) })}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Button size="small" startIcon={<Add />} sx={{ alignSelf: 'flex-start', color: '#00C896', textTransform: 'none' }}
                            onClick={() => patchOverride(idx, { slots: [...(o.slots || []), { open: '11:00', close: '22:00' }] })}>
                            {t('schedule.addSlot', 'Add time slot')}
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </Box>

            </Box>

            <Typography sx={{ color: '#666', fontSize: '0.78rem', mt: 2.5, fontStyle: 'italic' }}>
              {t('schedule.hint', 'These settings update the customer website immediately. Use for holidays, events, or one-off schedule changes.')}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #3a3d52' }}>
        <Button onClick={onClose} sx={{ color: '#9ca3af' }}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          variant="contained"
          sx={{ backgroundColor: '#00C896', color: '#1a1d2e', '&:hover': { backgroundColor: '#7bc912' }, fontWeight: 600 }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#1a1d2e' }} /> : t('schedule.save', 'Save schedule')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchScheduleDialog;
